import { useState, useEffect } from 'react';
import axios from 'axios';
import { Package, Plus, Trash2, DollarSign, Search, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MaterialsUsed = ({ encounterId, userRole, isSigned }) => {
  const [materials, setMaterials] = useState([]);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [addToInvoice, setAddToInvoice] = useState(false);

  useEffect(() => {
    fetchData();
  }, [encounterId]);

  const fetchData = async () => {
    try {
      const [materialsRes, itemsRes] = await Promise.all([
        axios.get(`${API}/encounters/${encounterId}/materials`),
        axios.get(`${API}/inventory/active`)
      ]);
      setMaterials(materialsRes.data);
      setInventoryItems(itemsRes.data);
    } catch (error) {
      console.error('Failed to fetch materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddMaterial = async () => {
    if (!selectedItem || quantity <= 0) {
      toast.error('Please select an item and enter quantity');
      return;
    }

    setAdding(true);
    try {
      await axios.post(`${API}/encounters/${encounterId}/materials`, {
        item_id: selectedItem.id,
        quantity: quantity,
        add_to_invoice: addToInvoice && selectedItem.is_billable
      });
      toast.success('Material added');
      setShowAddForm(false);
      setSelectedItem(null);
      setQuantity(1);
      setAddToInvoice(false);
      setSearchTerm('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add material');
    } finally {
      setAdding(false);
    }
  };

  const handleRemove = async (materialId) => {
    if (!window.confirm('Remove this material? Stock will be restored.')) return;
    try {
      await axios.delete(`${API}/encounters/${encounterId}/materials/${materialId}`);
      toast.success('Material removed');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove material');
    }
  };

  const filteredItems = inventoryItems.filter(item =>
    item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canAddMaterials = ['owner', 'doctor', 'nurse'].includes(userRole) && !isSigned;
  const canRemove = userRole === 'owner';

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package className="w-5 h-5 text-slate-600" />
          <h3 className="font-semibold text-slate-800">Materials Used</h3>
          {materials.length > 0 && (
            <span className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded-full">
              {materials.length}
            </span>
          )}
        </div>
        {canAddMaterials && (
          <Button size="sm" onClick={() => setShowAddForm(!showAddForm)}>
            <Plus className="w-4 h-4 mr-1" />
            Add Material
          </Button>
        )}
      </div>

      {/* Add Material Form */}
      {showAddForm && canAddMaterials && (
        <div className="p-4 bg-blue-50 rounded-lg border border-blue-200 space-y-3">
          <div>
            <Label>Search Inventory</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name or SKU..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setSelectedItem(null);
                }}
                className="pl-10"
              />
            </div>
          </div>

          {searchTerm && !selectedItem && (
            <div className="max-h-40 overflow-y-auto bg-white rounded border">
              {filteredItems.length === 0 ? (
                <p className="p-3 text-sm text-slate-500">No items found</p>
              ) : (
                filteredItems.slice(0, 10).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => {
                      setSelectedItem(item);
                      setSearchTerm(item.name);
                      setAddToInvoice(item.is_billable);
                    }}
                    className="w-full p-2 text-left hover:bg-slate-50 border-b last:border-b-0 flex items-center justify-between"
                  >
                    <div>
                      <p className="font-medium text-slate-800">{item.name}</p>
                      <p className="text-xs text-slate-500">{item.sku} • {item.current_quantity} {item.unit_of_measure} in stock</p>
                    </div>
                    {item.current_quantity <= item.moq && (
                      <AlertTriangle className="w-4 h-4 text-amber-500" title="Low stock" />
                    )}
                  </button>
                ))
              )}
            </div>
          )}

          {selectedItem && (
            <div className="p-3 bg-white rounded border space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-slate-800">{selectedItem.name}</p>
                  <p className="text-xs text-slate-500">
                    {selectedItem.sku} • {selectedItem.current_quantity} {selectedItem.unit_of_measure} in stock
                    {selectedItem.is_billable && ` • $${selectedItem.selling_price.toFixed(2)} each`}
                  </p>
                </div>
                <button onClick={() => { setSelectedItem(null); setSearchTerm(''); }} className="text-slate-400 hover:text-slate-600">
                  ×
                </button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Quantity</Label>
                  <Input
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={quantity}
                    onChange={(e) => setQuantity(parseFloat(e.target.value) || 0)}
                  />
                  <p className="text-xs text-slate-500 mt-1">{selectedItem.unit_of_measure}</p>
                </div>
                {selectedItem.is_billable && (
                  <div className="flex flex-col justify-center">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={addToInvoice}
                        onCheckedChange={setAddToInvoice}
                      />
                      <Label className="cursor-pointer">Add to Invoice</Label>
                    </div>
                    {addToInvoice && (
                      <p className="text-xs text-green-600 mt-1">
                        <DollarSign className="w-3 h-3 inline" />
                        ${(quantity * selectedItem.selling_price).toFixed(2)} will be added
                      </p>
                    )}
                  </div>
                )}
              </div>

              {selectedItem.current_quantity < quantity && (
                <p className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  Warning: This will result in negative stock
                </p>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowAddForm(false);
                    setSelectedItem(null);
                    setSearchTerm('');
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleAddMaterial}
                  disabled={adding || quantity <= 0}
                  className="flex-1"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                  Add Material
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Materials List */}
      {loading ? (
        <div className="text-center py-4 text-slate-500">Loading...</div>
      ) : materials.length === 0 ? (
        <div className="text-center py-6 text-slate-400 border-2 border-dashed rounded-lg">
          <Package className="w-8 h-8 mx-auto mb-2 opacity-50" />
          <p>No materials used yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {materials.map((mat) => (
            <div
              key={mat.id}
              className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-slate-200 rounded flex items-center justify-center">
                  <Package className="w-4 h-4 text-slate-600" />
                </div>
                <div>
                  <p className="font-medium text-slate-800">{mat.item_name}</p>
                  <p className="text-xs text-slate-500">
                    {mat.item_sku} • {mat.quantity} {mat.unit_of_measure}
                    {mat.added_to_invoice && (
                      <span className="ml-2 text-green-600">
                        <DollarSign className="w-3 h-3 inline" />
                        ${(mat.quantity * mat.unit_price).toFixed(2)} invoiced
                      </span>
                    )}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {mat.is_billable && !mat.added_to_invoice && (
                  <span className="text-xs text-slate-400 italic">Not invoiced</span>
                )}
                {canRemove && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRemove(mat.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MaterialsUsed;
